import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';

interface BearerTokenConnectionProps {
  onSuccess?: () => void;
}

export function BearerTokenConnection({ onSuccess }: BearerTokenConnectionProps) {
  const { toast } = useToast();
  const [bearerToken, setBearerToken] = useState('');
  const [email, setEmail] = useState('');
  const [orgId, setOrgId] = useState('');
  const [saveToEnv, setSaveToEnv] = useState(true);

  // Connect with OpenSolar using bearer token
  const { mutate: connect, isPending } = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/opensolar/connect/bearer-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: bearerToken,
          email,
          org_id: orgId,
          saveToEnv,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to connect with bearer token');
      }

      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'Connection Successful',
        description: data.message || 'Successfully connected to OpenSolar API',
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/opensolar/status'] });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Reset form
      setBearerToken('');
      setEmail('');
      setOrgId('');
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Connection Failed',
        description: error.message || 'Failed to connect to OpenSolar API',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!bearerToken || !email || !orgId) {
      toast({
        variant: 'destructive',
        title: 'Missing Fields',
        description: 'Please fill in all required fields',
      });
      return;
    }

    connect();
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Connect with Bearer Token</CardTitle>
        <CardDescription>
          Use an existing OpenSolar bearer token to connect directly
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bearerToken">Bearer Token</Label>
            <Input
              id="bearerToken"
              type="password"
              value={bearerToken}
              onChange={(e) => setBearerToken(e.target.value)}
              placeholder="Enter your OpenSolar bearer token"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your OpenSolar email"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="orgId">Organization ID</Label>
            <Input
              id="orgId"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              placeholder="Enter your OpenSolar Organization ID"
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch id="saveToEnv" checked={saveToEnv} onCheckedChange={setSaveToEnv} />
            <Label htmlFor="saveToEnv">Save as environment variables (recommended)</Label>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button onClick={handleSubmit} disabled={isPending}>
          {isPending ? 'Connecting...' : 'Connect'}
        </Button>
      </CardFooter>
    </Card>
  );
}
